import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export interface UseRecentRecipientListOrderOptions {
  storageKey?: string
  max?: number
  disableReorder?: boolean
  initialSelectedIds?: string[]
}

export interface UseRecentRecipientListOrderResult<T> {
  orderedLists: T[]
  applySelection: (selectedIds: string[]) => void
  recentIds: string[]
  resetRecent: () => void
}

const DEFAULT_STORAGE_KEY = 'recentRecipientListIdsOrder'
const DEFAULT_MAX = 50

function readOrder(key: string): string[] {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as string[]) : []
  } catch {
    return []
  }
}

function writeOrder(key: string, order: string[], max: number) {
  try {
    localStorage.setItem(key, JSON.stringify(order.slice(0, max)))
  } catch {
    // noop
  }
}

// Přijímá pole, které může obsahovat null/undefined položky a interně je filtruje.
export function useRecentRecipientListOrder<T extends { id?: string | null }>(
  listsInput: Array<T | null | undefined> | undefined,
  options?: UseRecentRecipientListOrderOptions
): UseRecentRecipientListOrderResult<T> {
  const storageKey = options?.storageKey || DEFAULT_STORAGE_KEY
  const max = options?.max ?? DEFAULT_MAX
  const disableReorder = options?.disableReorder ?? false
  const initialSelectedIds = options?.initialSelectedIds || []

  // Normalizované pole bez null
  const lists = useMemo<T[]>(
    () => (listsInput ? (listsInput.filter(Boolean) as T[]) : []),
    [listsInput]
  )

  const [recentIds, setRecentIds] = useState<string[]>(readOrder(storageKey))
  const prevSelectedRef = useRef<string[]>([...initialSelectedIds])

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === storageKey) setRecentIds(readOrder(storageKey))
    }
    window.addEventListener('storage', handler)
    return () => window.removeEventListener('storage', handler)
  }, [storageKey])

  const applySelection = useCallback(
    (selectedIds: string[]) => {
      const prev = prevSelectedRef.current
      const newlyAdded = selectedIds.filter(id => !prev.includes(id))
      prevSelectedRef.current = selectedIds
      if (disableReorder) return
      if (!newlyAdded.length) return
      setRecentIds(curr => {
        const cleaned = curr.filter(id => !newlyAdded.includes(id))
        for (let i = newlyAdded.length - 1; i >= 0; i--) {
          cleaned.unshift(newlyAdded[i])
        }
        writeOrder(storageKey, cleaned, max)
        return [...cleaned]
      })
    },
    [disableReorder, max, storageKey]
  )

  const resetRecent = useCallback(() => {
    try {
      localStorage.removeItem(storageKey)
    } catch {
      // noop
    }
    setRecentIds([])
  }, [storageKey])

  const orderedLists = useMemo(() => {
    if (!lists.length) return [] as T[]
    const byId: Record<string, T> = {}
    lists.forEach(list => {
      const id = list?.id
      if (id) byId[id] = list
    })
    const existingRecent = recentIds.filter(id => byId[id])
    const remaining = lists.filter(list => {
      const id = list?.id
      return id && !existingRecent.includes(id)
    })
    return [...existingRecent.map(id => byId[id]), ...remaining]
  }, [lists, recentIds])

  return { orderedLists, applySelection, recentIds, resetRecent }
}
